import {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import {
  clearData,
  executeActionWithDataId,
  findDataById,
  findRowsList,
  saveData,
} from '../../../actions/agGrid';
import { formBasicTab } from '../../../constants/components';
import { maxResultPerFilter } from '../../../constants/globals';
import { tableConfig } from '../../../constants/tables';
import { useModalToggle } from '../../../hooks/useModalToggle';
import { getLastTabWithSubmit, getNextTab } from '../../../utils/multiform';
import { ActionTypes } from '../../Administration/utils';
import { confirmDialog } from '../../Reusable/ConfirmDialog';
import LoadingIndicator from '../../Reusable/LoadingIndicator';
import CustomModal from '../../Reusable/Modal/CustomModal';
import DataProcessModal from '../Components/DataProcessModal';
import {
  getActionName,
  getConfirmDialogMessageAndAction,
  getMetaDataTypeTable,
  getModalTitle,
  getModelName,
  getOperationName,
  getTableNameSingular,
  getTranslations,
  isActionProcess,
} from '../action-utils';
import AgGridActionForm from './AgGridActionForm';

const AgGridAction = (props) => {
  const {
    gt,
    enums,
    action,
    setAction,
    gridRef,
    additionalFilterCriteria,
    tableName,
    inForm,
    selectedRow,
    initialSearch,
    setSelectedRow,
    hideSubmitButton = false,
    additionalProps,
    setRowsChecked,
    propertyForDelete,
  } = props || {};

  const { type } = useParams();
  const dispatch = useDispatch();

  const { translations: t } = useSelector((state) => ({
    translations: state.translationsReducer.translations,
  }));

  const translations = useMemo(
      () => getTranslations(tableName, t),
      [tableName, t]
    ),
    { ConfirmDialog, NotificationMessages, Modals } = translations || {};

  const submitRef = useRef();
  const [showModal, toggleModal] = useModalToggle(false);
  const [preview, setPreview] = useState(false);
  const [object, setObject] = useState(null);
  const [tab, setTab] = useState(formBasicTab);
  const [tabsConfigurationFunction, setTabsConfigurationFunction] = useState(
    () => () => []
  );
  const [tabsConfiguration, setTabsConfiguration] = useState([]);
  const { multiform, wideModal, name } = useMemo(
    () => Object.values(tableConfig)?.find((t) => t.name === tableName) || {},
    [tableName]
  );

  const modalTitle = useMemo(
    () => getModalTitle(action, Modals, tableName, object),
    [action, Modals, tableName, object]
  );

  const hideSubmitButtonSignal = useMemo(
    () =>
      tabsConfiguration?.find((t) => t.value === tab)?.hideSubmitButton ||
      preview ||
      hideSubmitButton,
    [tabsConfiguration, preview, tab, hideSubmitButton]
  );

  const showCloseButtonSignal = useMemo(
    () =>
      tabsConfiguration?.find((t) => t.value === tab)?.showCloseButton ||
      preview,
    [tabsConfiguration, tab, preview]
  );

  const getTabConfig = useCallback(async () => {
    const tableName = getTableNameSingular(name);
    const modelName = getModelName(tableName);
    await import(`../../${modelName}/${tableName}TabsConfig`).then(
      ({ tabsConfig }) => {
        setTabsConfigurationFunction(() => tabsConfig);
      }
    );
  }, [name]);

  const modalCloseHandler = useCallback(() => {
    setAction(null);
    toggleModal(false);
    preview && setPreview(false);
    dispatch(clearData(getTableNameSingular(tableName), inForm));
    setSelectedRow?.(null);
    gridRef.current?.api?.deselectAll();
    setRowsChecked([]);
    setTab(formBasicTab);
    setTabsConfigurationFunction(() => () => []);
    setTabsConfiguration([]);
  }, [
    dispatch,
    preview,
    gridRef,
    inForm,
    setAction,
    setRowsChecked,
    setSelectedRow,
    tableName,
    toggleModal,
  ]);

  const openModal = useCallback(() => {
    const dataId =
      gridRef?.current?.api?.selectionService?.lastSelectedNode?.data?.id;
    setObject(gridRef?.current?.api?.selectionService?.lastSelectedNode?.data);
    if (dataId) {
      dispatch(
        findDataById({
          id: dataId,
          tableName: getTableNameSingular(tableName),
          NotificationMessages,
          inForm,
        })
      );
    }
    if (multiform) {
      getTabConfig();
    }
    toggleModal();
  }, [
    gridRef,
    tableName,
    dispatch,
    NotificationMessages,
    inForm,
    toggleModal,
    getTabConfig,
    multiform,
  ]);

  useEffect(() => {
    if (initialSearch) {
      dispatch(
        findRowsList(
          getMetaDataTypeTable(type),
          additionalFilterCriteria,
          maxResultPerFilter[0],
          NotificationMessages,
          gridRef,
          inForm
        )
      );
    }
    // eslint-disable-next-line
  }, [
    dispatch,
    type,
    gridRef,
    inForm,
    NotificationMessages,
    openModal,
    initialSearch,
  ]);

  const submitCallbackHandler = useCallback(() => {
    if (
      submitRef?.current &&
      multiform &&
      tabsConfiguration?.length > 1 &&
      tab !== getLastTabWithSubmit(tabsConfiguration)?.value
    ) {
      const nextTab = getNextTab(tab, tabsConfiguration);
      nextTab && setTab(nextTab);
      action === ActionTypes.CREATE && setAction(ActionTypes.UPDATE);
    } else {
      modalCloseHandler();
    }
  }, [action, multiform, tabsConfiguration, tab, modalCloseHandler, setAction]);

  const onSubmitHandler = (requestData, additionalParams) => {
    setObject(requestData);
    dispatch(
      saveData({
        data: requestData,
        tableName: getTableNameSingular(tableName),
        messages: NotificationMessages,
        callback: submitCallbackHandler,
        additionalParams,
        action: getActionName(action, tableName),
        operation: action?.toLowerCase(),
        addToModalSignal: inForm,
      })
    );
  };

  const confirmActionHandler = useCallback(() => {
    const data =
      gridRef?.current?.api?.selectionService?.lastSelectedNode?.data;

    const { confirmMessage, confirmAction } = getConfirmDialogMessageAndAction(
      action,
      ConfirmDialog,
      tableName,
      data
    );
    const dataId = data?.id;
    let deleteProperty;
    if (propertyForDelete) {
      deleteProperty = data[propertyForDelete];
    }

    const confirmDialogAction = () => {
      dispatch(
        executeActionWithDataId({
          id: dataId || deleteProperty,
          additionalProps,
          tableName: getTableNameSingular(tableName),
          action: confirmAction,
          operation: getOperationName(action),
          messages: NotificationMessages,
          deleteToModal: inForm,
          setSelectedRow,
        })
      );
    };
    if (dataId || propertyForDelete) {
      confirmDialog(confirmMessage, confirmDialogAction, '', gt);
      setAction(null);
      setSelectedRow?.(null);
      inForm && gridRef.current?.api?.deselectAll();
    }
  }, [
    gridRef,
    gt,
    setAction,
    dispatch,
    NotificationMessages,
    tableName,
    action,
    inForm,
    setSelectedRow,
    additionalProps,
    ConfirmDialog,
    propertyForDelete,
  ]);

  const actionHandler = useCallback(() => {
    switch (action) {
      case ActionTypes.CREATE:
      case ActionTypes.UPDATE:
      case ActionTypes.PREVIEW:
        if (action === ActionTypes.PREVIEW) setPreview(true);
        openModal();
        break;
      case ActionTypes.ACTIVATE:
      case ActionTypes.DEACTIVATE:
      case ActionTypes.DELETE:
      case ActionTypes.ACTIVATE2FA:
      case ActionTypes.DEACTIVATE2FA:
        confirmActionHandler();
        break;
      case ActionTypes.PROCESS:
      case ActionTypes.PROCESS1:
      case ActionTypes.PROCESS2:
      case ActionTypes.SHOW_CHILDREN:
      case ActionTypes.PASSWORD_CHANGE:
        openModal();
        break;
      default:
        break;
    }
  }, [action, openModal, confirmActionHandler]);

  useEffect(() => {
    action && !showModal && actionHandler();
  }, [action, actionHandler, tableName, showModal]);

  return (
    <Fragment>
      {showModal && (
        <Suspense
          fallback={
            <LoadingIndicator loading={true} showOnComponentLevel={true} />
          }
        >
          <CustomModal
            show={
              showModal &&
              !isActionProcess(action) &&
              action !== ActionTypes.PASSWORD_CHANGE
            }
            hideModal={modalCloseHandler}
            title={modalTitle}
            submitRef={submitRef}
            wideModal={wideModal}
            hideSaveButton={hideSubmitButtonSignal}
            showCloseButton={showCloseButtonSignal}
            secondModal={inForm}
            isView={preview}
          >
            <AgGridActionForm
              gt={gt}
              enums={enums}
              translations={translations}
              tableName={tableName}
              submitRef={submitRef}
              tabRef={{
                tab,
                setTab,
                tabsConfig: tabsConfigurationFunction,
                setTabsConfiguration,
              }}
              onSubmit={onSubmitHandler}
              isView={preview}
              inForm={inForm}
              action={action}
            />
          </CustomModal>
        </Suspense>
      )}
      {(isActionProcess(action) ||
        action === ActionTypes.SHOW_CHILDREN ||
        action === ActionTypes.PASSWORD_CHANGE) && (
        <DataProcessModal
          show={
            showModal &&
            (isActionProcess(action) ||
              action === ActionTypes.SHOW_CHILDREN ||
              action === ActionTypes.PASSWORD_CHANGE)
          }
          hideModal={modalCloseHandler}
          tableName={tableName}
          action={action}
          messages={NotificationMessages}
          title={modalTitle}
          translations={translations}
          gt={gt}
          gridRef={gridRef}
          submitRef={submitRef}
          type={type}
          selectedRow={selectedRow}
          isView={preview}
        />
      )}
    </Fragment>
  );
};

export default AgGridAction;
